import React, { useState, useEffect } from 'react';
import { QuizzesTab, CulturalTipsTab, DarijaPhrasesTab, WheelSettingsTab, AnalyticsTab } from '../GamificationTabs';

/* Image picker — browse folders from Image Manager, pick an image */
const IconPickerGrid = ({ API, adminToken, isDarkMode, selectedUrl, onSelect }) => {
  const [folders, setFolders] = useState([]);
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [assets, setAssets] = useState([]);
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const base = API.replace('/api', '');

  useEffect(() => {
    (async () => {
      try {
        const [foldersRes, iconsRes] = await Promise.all([
          fetch(`${API}/admin/image-folders`, { headers: { 'Authorization': `Bearer ${adminToken}` } }),
          fetch(`${API}/admin/icons`, { headers: { 'Authorization': `Bearer ${adminToken}` } })
        ]);
        if (foldersRes.ok) { const d = await foldersRes.json(); setFolders(d.folders || []); }
        if (iconsRes.ok) { const d = await iconsRes.json(); setIcons(d.icons || []); }
      } catch (e) { console.error('Failed to load', e); }
      setLoading(false);
    })();
  }, [API, adminToken]);

  const loadAssets = async (folderId) => {
    setActiveFolderId(folderId);
    setAssetsLoading(true);
    try {
      const res = await fetch(`${API}/admin/image-assets/${folderId}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) { const d = await res.json(); setAssets(d.assets || []); }
    } catch (e) { console.error('Failed to load assets', e); }
    setAssetsLoading(false);
  };

  const currentImages = activeFolderId ? assets : icons;
  const filtered = search
    ? currentImages.filter(i => (i.name || '').toLowerCase().includes(search.toLowerCase()))
    : currentImages;

  const tabCls = (active) => `px-3 py-1.5 text-xs rounded-lg font-medium transition-all whitespace-nowrap ${
    active
      ? 'bg-blue-500 text-white'
      : isDarkMode ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-600 hover:bg-gray-100'
  }`;

  return (
    <div className={`mt-2 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
      {loading ? (
        <p className={`text-center text-sm py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</p>
      ) : (
        <>
          {/* Folder tabs */}
          <div className="flex gap-1.5 overflow-x-auto pb-2 mb-2">
            <button type="button" onClick={() => { setActiveFolderId(null); setSearch(''); }} className={tabCls(!activeFolderId)}>
              Icons
            </button>
            {folders.map(f => (
              <button key={f.id} type="button" onClick={() => { loadAssets(f.id); setSearch(''); }} className={tabCls(activeFolderId === f.id)}>
                {f.icon || '📁'} {f.name}
              </button>
            ))}
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search images..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`w-full px-3 py-1.5 mb-2 rounded border text-sm ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-gray-50 border-gray-300'}`}
          />

          {/* Image grid */}
          {assetsLoading ? (
            <p className={`text-center text-sm py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading images...</p>
          ) : filtered.length === 0 ? (
            <p className={`text-center text-sm py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No images found.</p>
          ) : (
            <div className="max-h-56 overflow-y-auto">
              <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5">
                {filtered.map(img => (
                  <button
                    key={img.id || img.url}
                    type="button"
                    onClick={() => onSelect(img.url)}
                    title={img.name}
                    className={`p-1.5 rounded-lg transition-all hover:scale-110 ${
                      selectedUrl === img.url ? 'ring-2 ring-blue-500 bg-blue-500/20' : isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-100'
                    }`}
                  >
                    <img src={`${base}${img.url}`} alt={img.name} className="w-10 h-10 object-contain" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const SoukGameAdmin = ({ API, adminToken, showNotification, isDarkMode }) => {
  const [activeSubTab, setActiveSubTab] = useState('recipes');
  const [recipes, setRecipes] = useState([]);
  const [items, setItems] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [gameTexts, setGameTexts] = useState(null);
  const [gameSettings, setGameSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Gamification states
  const [quizzes, setQuizzes] = useState([]);
  const [culturalTips, setCulturalTips] = useState([]);
  const [darijaPhras, setDarijaPhrases] = useState([]);
  const [wheelSettings, setWheelSettings] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // {type, id, name}
  const [editType, setEditType] = useState('');

  useEffect(() => {
    if (activeSubTab === 'settings') loadGameSettings();
    if (activeSubTab === 'recipes') loadRecipes();
    if (activeSubTab === 'items') { loadItems(); if (!gameSettings) loadGameSettings(); }
    if (activeSubTab === 'merchants') { loadMerchants(); if (!gameSettings) loadGameSettings(); }
    if (activeSubTab === 'game-texts') loadGameTexts();
    if (activeSubTab === 'quizzes') loadQuizzes();
    if (activeSubTab === 'tips') loadCulturalTips();
    if (activeSubTab === 'phrases') loadDarijaPhrases();
    if (activeSubTab === 'wheel') loadWheelSettings();
    if (activeSubTab === 'analytics') loadAnalytics();
  }, [activeSubTab]);

  const loadRecipes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/souk/recipes`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      setRecipes(data);
    } catch (error) {
      showNotification?.('Failed to load recipes', 'error');
    }
    setLoading(false);
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/souk/items`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      setItems(data);
    } catch (error) {
      showNotification?.('Failed to load items', 'error');
    }
    setLoading(false);
  };

  const loadMerchants = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/souk/merchants`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      setMerchants(data);
    } catch (error) {
      showNotification?.('Failed to load merchants', 'error');
    }
    setLoading(false);
  };

  const loadGameTexts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/souk/game-texts`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      setGameTexts(data);
    } catch (error) {
      showNotification?.('Failed to load game texts', 'error');
    }
    setLoading(false);
  };

  const loadGameSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/souk/game-settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      setGameSettings(data);
    } catch (error) {
      showNotification?.('Failed to load game settings', 'error');
    }
    setLoading(false);
  };

  // Gamification load functions
  const loadQuizzes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/souk-clothes/quizzes`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      setQuizzes(data);
    } catch (error) {
      showNotification?.('Failed to load quizzes', 'error');
    }
    setLoading(false);
  };

  const loadCulturalTips = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/souk-clothes/cultural-tips`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      setCulturalTips(data);
    } catch (error) {
      showNotification?.('Failed to load cultural tips', 'error');
    }
    setLoading(false);
  };

  const loadDarijaPhrases = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/souk-clothes/darija-phrases`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      setDarijaPhrases(data);
    } catch (error) {
      showNotification?.('Failed to load Darija phrases', 'error');
    }
    setLoading(false);
  };

  const loadWheelSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/souk-clothes/wheel-settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      setWheelSettings(data);
    } catch (error) {
      showNotification?.('Failed to load wheel settings', 'error');
    }
    setLoading(false);
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/souk-clothes/analytics`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      showNotification?.('Failed to load analytics', 'error');
    }
    setLoading(false);
  };

  const saveWheelSettings = async () => {
    try {
      await fetch(`${API}/admin/souk-clothes/wheel-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(wheelSettings)
      });
      showNotification?.('Wheel settings saved successfully', 'success');
    } catch (error) {
      showNotification?.('Failed to save wheel settings', 'error');
    }
  };


  const saveGameSettings = async () => {
    console.log('Saving game settings:', gameSettings);
    
    // Clean data - only send the fields that should be updated
    const cleanData = {
      is_active: gameSettings.is_active,
      is_pro_only: gameSettings.is_pro_only,
      name: gameSettings.name,
      description: gameSettings.description
    };
    
    console.log('Clean data to send:', cleanData);
    
    try {
      const response = await fetch(`${API}/admin/souk/game-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cleanData)
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('Save result:', result);
        showNotification?.('Game settings updated successfully! Changes will apply immediately.', 'success');
        // Reload settings to confirm
        await loadGameSettings();
      } else {
        const error = await response.text();
        console.error('Error saving:', error);
        showNotification?.('Failed to update game settings: ' + error, 'error');
      }
    } catch (error) {
      console.error('Save error:', error);
      showNotification?.('Failed to update game settings: ' + error.message, 'error');
    }
  };

  const handleCreate = (type) => {
    setIsCreating(true);
    if (type === 'recipe') {
      setEditingItem({
        name_english: '',
        name_darija: '',
        name_arabic: '',
        description: '',
        category: 'Food',
        difficulty: 'Medium',
        cooking_time_minutes: 30,
        serves: 4,
        estimated_cost_dh: 50,
        required_items: [],
        cooking_instructions: ''
      });
    } else if (type === 'item') {
      setEditingItem({
        name_english: '',
        name_darija: '',
        name_arabic: '',
        category: 'Vegetable',
        subcategory: 'Fresh',
        base_price_tokens: 10,
        unit: 'kg',
        is_active: true
      });
    } else if (type === 'merchant') {
      setEditingItem({
        name: '',
        category: 'Vegetable',
        personality: 'helpful',
        location: 'Main Souk',
        greeting_darija: '',
        greeting_english: '',
        description: '',
        avatar_url: '',
        discount_range_min: 5,
        discount_range_max: 25,
        price_multiplier: 1.0,
        is_active: true
      });
    }
    setShowEditModal(true);
  };

  const handleEdit = (item) => {
    if (!gameSettings) loadGameSettings();
    setIsCreating(false);
    setEditingItem({...item});
    setShowEditModal(true);
  };

  const handleSave = async () => {
    try {
      const collection = activeSubTab;
      const method = isCreating ? 'POST' : 'PUT';
      const url = isCreating 
        ? `${API}/admin/souk/${collection}`
        : `${API}/admin/souk/${collection}/${editingItem.id}`;

      console.log('Saving:', { method, url, data: editingItem });

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editingItem)
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        showNotification?.(isCreating ? 'Created successfully' : 'Updated successfully', 'success');
        setShowEditModal(false);
        setEditingItem(null);
        if (collection === 'recipes') loadRecipes();
        if (collection === 'items') loadItems();
        if (collection === 'merchants') loadMerchants();
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        try {
          const error = JSON.parse(errorText);
          showNotification?.(error.detail || error.message || 'Failed to save', 'error');
        } catch {
          showNotification?.(`Failed to save: ${response.status} ${response.statusText}`, 'error');
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      showNotification?.(`Failed to save: ${error.message}`, 'error');
    }
  };


  const handleGamificationSave = async () => {
    try {
      const endpoint = editType === 'quiz' ? 'quizzes' 
        : editType === 'tip' ? 'cultural-tips'
        : 'darija-phrases';
      
      const method = editingItem.id ? 'PUT' : 'POST';
      const url = editingItem.id 
        ? `${API}/admin/souk-clothes/${endpoint}/${editingItem.id}`
        : `${API}/admin/souk-clothes/${endpoint}`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editingItem)
      });

      if (response.ok) {
        showNotification?.('Saved successfully', 'success');
        setShowEditModal(false);
        setEditingItem(null);
        if (editType === 'quiz') loadQuizzes();
        if (editType === 'tip') loadCulturalTips();
        if (editType === 'phrase') loadDarijaPhrases();
      } else {
        showNotification?.('Failed to save', 'error');
      }
    } catch (error) {
      showNotification?.(`Failed to save: ${error.message}`, 'error');
    }
  };


  const confirmDelete = async () => {
    const { collection, id } = showDeleteConfirm;
    
    try {
      const response = await fetch(`${API}/admin/souk/${collection}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      if (response.ok) {
        showNotification?.('Deleted successfully', 'success');
        setShowDeleteConfirm(null);
        if (collection === 'recipes') loadRecipes();
        if (collection === 'items') loadItems();
        if (collection === 'merchants') loadMerchants();
      }
    } catch (error) {
      showNotification?.('Failed to delete', 'error');
    }
  };

  const saveGameTexts = async () => {
    try {
      const { _showIconPicker, ...textsToSave } = gameTexts;
      const response = await fetch(`${API}/admin/souk/game-texts`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(textsToSave)
      });

      if (response.ok) {
        showNotification?.('Game texts updated successfully', 'success');
      }
    } catch (error) {
      showNotification?.('Failed to update game texts', 'error');
    }
  };

  const subTabs = [
    { id: 'settings', label: 'Settings', icon: '⚙️' },
    { id: 'recipes', label: 'Recipes', icon: '🍽️' },
    { id: 'items', label: 'Items', icon: '🥕' },
    { id: 'merchants', label: 'Merchants', icon: '👨‍💼' },
    { id: 'game-texts', label: 'Game Texts', icon: '📝' },
    { id: 'stats', label: 'User Statistics', icon: '📈' }
  ];

  return (
    <div className="space-y-6">
      <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
        🍽️ D-Bargain Souk Management (Food Game)
      </h3>

      {/* Main Layout - Sidebar + Content */}
      <div className="flex gap-6">
        {/* Left Sidebar - Vertical Tabs */}
        <div className={`w-64 flex-shrink-0 rounded-xl shadow-lg overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="p-4">
            <h3 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              SECTIONS
            </h3>
            <div className="space-y-1">
              {subTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveSubTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all text-left ${
                    activeSubTab === tab.id
                      ? isDarkMode
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-blue-500 text-white shadow-lg'
                      : isDarkMode
                        ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <span className="text-xl">{tab.icon}</span>
                  <span className="text-sm">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Content Area */}
        <div className={`flex-1 rounded-xl shadow-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Settings Tab */}
        {activeSubTab === 'settings' && (
          loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className={`mt-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading settings...</p>
            </div>
          ) : gameSettings ? (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h4 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Game Settings & Access Control
              </h4>
              <button
                onClick={saveGameSettings}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:shadow-lg transition-all"
              >
                💾 Save Settings
              </button>
            </div>

            <div className="space-y-6">
              {/* Game Card Text */}
              <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <h5 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  🏷️ Game Card Text
                </h5>
                <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Edit the name and description shown on the Games page card
                </p>
                <div className="space-y-3">
                  <div>
                    <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Game Name</label>
                    <input
                      value={gameSettings.name || ''}
                      onChange={e => setGameSettings({...gameSettings, name: e.target.value})}
                      className={`w-full px-3 py-1.5 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'} border focus:border-blue-500 focus:outline-none`}
                    />
                  </div>
                  <div>
                    <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Description</label>
                    <input
                      value={gameSettings.description || ''}
                      onChange={e => setGameSettings({...gameSettings, description: e.target.value})}
                      className={`w-full px-3 py-1.5 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'} border focus:border-blue-500 focus:outline-none`}
                    />
                  </div>
                </div>
              </div>

              {/* Visibility Setting */}
              <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h5 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      👁️ Game Visibility
                    </h5>
                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Control whether the Souk Game is visible to users in the Games section
                    </p>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setGameSettings({...gameSettings, is_active: true})}
                        className={`px-6 py-3 rounded-lg font-medium transition-all ${
                          gameSettings.is_active
                            ? 'bg-green-600 text-white shadow-lg'
                            : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        ✅ Visible
                      </button>
                      <button
                        onClick={() => setGameSettings({...gameSettings, is_active: false})}
                        className={`px-6 py-3 rounded-lg font-medium transition-all ${
                          !gameSettings.is_active
                            ? 'bg-red-600 text-white shadow-lg'
                            : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        🚫 Hidden
                      </button>
                    </div>
                    {!gameSettings.is_active && (
                      <div className={`mt-3 p-3 rounded ${isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-50 text-red-800'}`}>
                        <p className="text-sm font-bold">⚠️ Game is currently HIDDEN from all users</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Access Control Setting */}
              <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h5 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      💎 Access Level
                    </h5>
                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Choose who can play the Souk Game
                    </p>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setGameSettings({...gameSettings, is_pro_only: false})}
                        className={`px-6 py-3 rounded-lg font-medium transition-all ${
                          !gameSettings.is_pro_only
                            ? 'bg-blue-600 text-white shadow-lg'
                            : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        🆓 Free for All Users
                      </button>
                      <button
                        onClick={() => setGameSettings({...gameSettings, is_pro_only: true})}
                        className={`px-6 py-3 rounded-lg font-medium transition-all ${
                          gameSettings.is_pro_only
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                            : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        👑 Pro Users Only
                      </button>
                    </div>
                    {gameSettings.is_pro_only && (
                      <div className={`mt-3 p-3 rounded ${isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-50 text-purple-800'}`}>
                        <p className="text-sm font-bold">👑 Only Pro/Premium members can access this game</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Current Status Summary */}
              <div className={`p-6 rounded-lg border-2 ${
                isDarkMode ? 'bg-gray-800 border-blue-600' : 'bg-blue-50 border-blue-400'
              }`}>
                <h5 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  📊 Current Status
                </h5>
                <div className="grid grid-cols-2 gap-4">
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
                    <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Visibility</p>
                    <p className={`text-lg font-bold ${gameSettings.is_active ? 'text-green-600' : 'text-red-600'}`}>
                      {gameSettings.is_active ? '✅ Visible' : '🚫 Hidden'}
                    </p>
                  </div>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
                    <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Access Level</p>
                    <p className={`text-lg font-bold ${gameSettings.is_pro_only ? 'text-purple-600' : 'text-blue-600'}`}>
                      {gameSettings.is_pro_only ? '👑 Pro Only' : '🆓 Free'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Merchant & Item Categories Management */}
              <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <h5 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  🏷️ Merchant & Item Categories
                </h5>
                <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Manage categories used for merchants and items. These appear in dropdown menus when creating/editing merchants and items.
                </p>
                <div className="space-y-2 mb-4">
                  {(gameSettings.merchant_categories || ['Vegetable', 'Meat', 'Spice', 'Shop']).map((cat, idx) => (
                    <div key={idx} className={`flex items-center gap-2 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
                      <input
                        type="text"
                        value={cat}
                        onChange={(e) => {
                          const cats = [...(gameSettings.merchant_categories || ['Vegetable', 'Meat', 'Spice', 'Shop'])];
                          cats[idx] = e.target.value;
                          setGameSettings({...gameSettings, merchant_categories: cats});
                        }}
                        className={`flex-1 px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-gray-50 border-gray-300'}`}
                      />
                      <button
                        onClick={() => {
                          const cats = [...(gameSettings.merchant_categories || ['Vegetable', 'Meat', 'Spice', 'Shop'])];
                          if (cats.length <= 1) return;
                          cats.splice(idx, 1);
                          setGameSettings({...gameSettings, merchant_categories: cats});
                        }}
                        className="px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors text-sm font-bold"
                        title="Remove category"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => {
                    const cats = [...(gameSettings.merchant_categories || ['Vegetable', 'Meat', 'Spice', 'Shop']), ''];
                    setGameSettings({...gameSettings, merchant_categories: cats});
                  }}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all text-sm font-medium"
                >
                  + Add Category
                </button>
                <p className={`text-xs mt-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Click "Save Settings" above to persist changes.
                </p>
              </div>
            </div>
          </div>
          ) : (
            <div className="text-center py-12">
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Failed to load settings. Please refresh the page.
              </p>
            </div>
          )
        )}

        {/* Recipes Tab */}
        {activeSubTab === 'recipes' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h4 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Recipes Management ({recipes.length} total)
              </h4>
              <button
                onClick={() => handleCreate('recipe')}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:shadow-lg transition-all"
              >
                + Add Recipe
              </button>
            </div>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recipes.map(recipe => (
                  <div key={recipe.id} className={`border rounded-lg p-4 hover:shadow-lg transition-all ${
                    isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h5 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {recipe.name_english}
                        </h5>
                        <p className="text-sm text-blue-600 italic">{recipe.name_darija}</p>
                      </div>
                      <span className="text-2xl">🍽️</span>
                    </div>
                    <div className={`space-y-2 text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <p><span className="font-semibold">Cost:</span> {recipe.estimated_cost_dh} DH</p>
                      <p><span className="font-semibold">Ingredients:</span> {recipe.required_items?.length || 0}</p>
                      <p><span className="font-semibold">Difficulty:</span> {recipe.difficulty || 'Medium'}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(recipe)}
                        className="flex-1 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-all text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm({ collection: 'recipes', id: recipe.id, name: recipe.name_english })}
                        className="flex-1 px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-all text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Items Tab - Similar structure with edit/delete */}
        {activeSubTab === 'items' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h4 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Items Management ({items.length} total)
              </h4>
              <button
                onClick={() => handleCreate('item')}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:shadow-lg transition-all"
              >
                + Add Item
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={isDarkMode ? 'bg-gray-600' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Item</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Category</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Price</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Unit</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDarkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                    {items.map(item => (
                      <tr key={item.id} className={isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-50'}>
                        <td className="px-4 py-3">
                          <div>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.name_english}</p>
                            <p className="text-sm text-blue-600 italic">{item.name_darija}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            item.category === 'Vegetable' ? 'bg-green-100 text-green-800' :
                            item.category === 'Meat' ? 'bg-red-100 text-red-800' :
                            item.category === 'Spice' ? 'bg-orange-100 text-orange-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {item.category}
                          </span>
                        </td>
                        <td className={`px-4 py-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.base_price_tokens} DH</td>
                        <td className={`px-4 py-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{item.unit}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(item)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => setShowDeleteConfirm({ collection: 'items', id: item.id, name: item.name_english })}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Merchants Tab */}
        {activeSubTab === 'merchants' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h4 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Merchants Management ({merchants.length} total)
              </h4>
              <button
                onClick={() => handleCreate('merchant')}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:shadow-lg transition-all"
              >
                + Add Merchant
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {merchants.map(merchant => (
                  <div key={merchant.id} className={`border rounded-lg p-4 hover:shadow-lg transition-all ${
                    isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="flex items-start gap-3 mb-3">
                      {merchant.avatar_url ? (
                        <img src={merchant.avatar_url} alt={merchant.name} className="w-14 h-14 rounded-full object-cover border-2 border-gray-200 flex-shrink-0" />
                      ) : (
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl flex-shrink-0 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                          {merchant.category === 'Vegetable' ? '🥕' : merchant.category === 'Meat' ? '🍖' : merchant.category === 'Spice' ? '🌶️' : '🏪'}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h5 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{merchant.name}</h5>
                        <p className={`text-sm capitalize ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{merchant.personality}</p>
                      </div>
                    </div>
                    <div className={`space-y-1 text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <p><span className="font-semibold">Category:</span> {merchant.category}</p>
                      <p><span className="font-semibold">Location:</span> {merchant.location || 'Main Souk'}</p>
                      <p><span className="font-semibold">Discount:</span> {merchant.discount_range_min || 5}% - {merchant.discount_range_max || 25}%</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(merchant)}
                        className="flex-1 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-all text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm({ collection: 'merchants', id: merchant.id, name: merchant.name })}
                        className="flex-1 px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-all text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Game Texts Tab */}
        {activeSubTab === 'game-texts' && gameTexts && (() => {
          const inputCls = `w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`;
          const labelCls = `block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`;
          const sectionCls = `p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`;
          const headingCls = `font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`;
          const tf = (key) => ({
            value: gameTexts[key] || '',
            onChange: (e) => setGameTexts({...gameTexts, [key]: e.target.value}),
            className: inputCls,
          });
          return (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h4 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Edit All Game Text & Messages
              </h4>
              <button onClick={saveGameTexts} className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:shadow-lg transition-all">
                💾 Save All Changes
              </button>
            </div>

            <div className="space-y-4">
              {/* Landing Page */}
              <div className={sectionCls}>
                <h5 className={headingCls}>Landing Page</h5>
                <div className="space-y-2">
                  {/* Icon Picker from Image Manager */}
                  <div>
                    <label className={labelCls}>Landing Icon</label>
                    <div className="flex items-center gap-3">
                      {gameTexts.landing_icon ? (
                        <img src={`${API.replace('/api', '')}${gameTexts.landing_icon}`} alt="Landing icon" className="w-16 h-16 object-contain rounded-lg" />
                      ) : (
                        <div className={`w-16 h-16 rounded-lg flex items-center justify-center text-sm ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>No icon</div>
                      )}
                      <div>
                        <button
                          type="button"
                          onClick={() => setGameTexts({...gameTexts, _showIconPicker: !gameTexts._showIconPicker})}
                          className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-all ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-white hover:bg-gray-100 border border-gray-300 text-gray-700'}`}
                        >
                          {gameTexts._showIconPicker ? 'Close Picker' : 'Choose from Image Manager'}
                        </button>
                        {gameTexts.landing_icon && (
                          <button
                            type="button"
                            onClick={() => setGameTexts({...gameTexts, landing_icon: ''})}
                            className="ml-2 px-3 py-1.5 text-sm rounded-lg font-medium text-red-500 hover:bg-red-50 transition-all"
                          >
                            Remove
                          </button>
                        )}
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Shown on the landing page hero</p>
                      </div>
                    </div>
                    {gameTexts._showIconPicker && (
                      <IconPickerGrid
                        API={API}
                        adminToken={adminToken}
                        isDarkMode={isDarkMode}
                        selectedUrl={gameTexts.landing_icon}
                        onSelect={(url) => setGameTexts({...gameTexts, landing_icon: url, _showIconPicker: false})}
                      />
                    )}
                  </div>
                  <div><label className={labelCls}>Title</label><input type="text" {...tf('landing_title')} /></div>
                  <div><label className={labelCls}>Subtitle</label><input type="text" {...tf('landing_subtitle')} /></div>
                  <div><label className={labelCls}>Description</label><input type="text" {...tf('landing_description')} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className={labelCls}>Button Text</label><input type="text" {...tf('landing_button')} /></div>
                    <div><label className={labelCls}>No Recipes Yet Text</label><input type="text" {...tf('landing_cooked_zero')} /></div>
                  </div>
                  <div><label className={labelCls}>Cooked Counter (use {'{count}'} for number)</label><input type="text" {...tf('landing_cooked')} /></div>
                </div>
              </div>

              {/* Recipe Selection */}
              <div className={sectionCls}>
                <h5 className={headingCls}>📖 Recipe Selection Page</h5>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className={labelCls}>Page Title</label><input type="text" {...tf('recipe_select_title')} /></div>
                  <div><label className={labelCls}>Subtitle</label><input type="text" {...tf('recipe_select_subtitle')} /></div>
                  <div><label className={labelCls}>Back Button</label><input type="text" {...tf('recipe_select_back')} /></div>
                  <div><label className={labelCls}>Loading Text</label><input type="text" {...tf('recipe_select_loading')} /></div>
                  <div><label className={labelCls}>How to Play Button</label><input type="text" {...tf('recipe_select_how_to_play')} /></div>
                  <div><label className={labelCls}>My Progress Button</label><input type="text" {...tf('recipe_select_my_progress')} /></div>
                </div>
              </div>

              {/* Recipe Confirmation */}
              <div className={sectionCls}>
                <h5 className={headingCls}>🛒 Recipe Confirmation Popup</h5>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className={labelCls}>Budget Label</label><input type="text" {...tf('recipe_confirm_budget')} /></div>
                  <div><label className={labelCls}>Start Button</label><input type="text" {...tf('recipe_confirm_start')} /></div>
                  <div className="col-span-2"><label className={labelCls}>Tip Text</label><input type="text" {...tf('recipe_confirm_tip')} /></div>
                  <div className="col-span-2"><label className={labelCls}>Instructions ({'{item_count}'}, {'{merchant_count}'})</label><input type="text" {...tf('recipe_confirm_instructions')} /></div>
                </div>
              </div>

              {/* Sticky Bar */}
              <div className={sectionCls}>
                <h5 className={headingCls}>📌 Sticky Top Bar</h5>
                <div className="grid grid-cols-4 gap-2">
                  <div><label className={labelCls}>Wallet</label><input type="text" {...tf('sticky_wallet')} /></div>
                  <div><label className={labelCls}>Checkout</label><input type="text" {...tf('sticky_checkout')} /></div>
                  <div><label className={labelCls}>Items Left ({'{count}'})</label><input type="text" {...tf('sticky_items_left')} /></div>
                  <div><label className={labelCls}>Reset</label><input type="text" {...tf('sticky_reset')} /></div>
                </div>
              </div>

              {/* Shopping List */}
              <div className={sectionCls}>
                <h5 className={headingCls}>📋 Shopping List Labels</h5>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className={labelCls}>List Title</label><input type="text" {...tf('shopping_list_title')} /></div>
                  <div><label className={labelCls}>Vegetables</label><input type="text" {...tf('shopping_list_vegetables')} /></div>
                  <div><label className={labelCls}>Meat</label><input type="text" {...tf('shopping_list_meat')} /></div>
                  <div><label className={labelCls}>Spices</label><input type="text" {...tf('shopping_list_spices')} /></div>
                  <div><label className={labelCls}>Shop Items</label><input type="text" {...tf('shopping_list_shop')} /></div>
                </div>
              </div>

              {/* Souk / Merchant Browsing */}
              <div className={sectionCls}>
                <h5 className={headingCls}>🏪 Souk & Merchants</h5>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className={labelCls}>Souk Title</label><input type="text" {...tf('souk_title')} /></div>
                    <div><label className={labelCls}>Back to Recipes</label><input type="text" {...tf('souk_back')} /></div>
                    <div><label className={labelCls}>Enter Shop Button</label><input type="text" {...tf('souk_enter_shop')} /></div>
                    <div><label className={labelCls}>Back to Souk</label><input type="text" {...tf('souk_back_to_souk')} /></div>
                  </div>
                  <div><label className={labelCls}>Instructions</label><input type="text" {...tf('souk_instructions')} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className={labelCls}>Vegetable Sellers</label><input type="text" {...tf('category_vegetable')} /></div>
                    <div><label className={labelCls}>Meat Sellers</label><input type="text" {...tf('category_meat')} /></div>
                    <div><label className={labelCls}>Spice Sellers</label><input type="text" {...tf('category_spice')} /></div>
                    <div><label className={labelCls}>General Shops</label><input type="text" {...tf('category_shop')} /></div>
                  </div>
                </div>
              </div>

              {/* Item Actions */}
              <div className={sectionCls}>
                <h5 className={headingCls}>🏷️ Item Buttons & Labels</h5>
                <div className="grid grid-cols-3 gap-2">
                  <div><label className={labelCls}>Needed Badge</label><input type="text" {...tf('item_needed')} /></div>
                  <div><label className={labelCls}>In Basket</label><input type="text" {...tf('item_in_basket')} /></div>
                  <div><label className={labelCls}>Add Button</label><input type="text" {...tf('item_add')} /></div>
                  <div><label className={labelCls}>Bargain Button</label><input type="text" {...tf('item_bargain')} /></div>
                  <div><label className={labelCls}>Compare Button</label><input type="text" {...tf('item_compare')} /></div>
                </div>
              </div>

              {/* Bargaining */}
              <div className={sectionCls}>
                <h5 className={headingCls}>💰 Bargaining Modal</h5>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className={labelCls}>Title ({'{merchant}'})</label><input type="text" {...tf('bargain_title')} /></div>
                  <div><label className={labelCls}>Asking Price Label</label><input type="text" {...tf('bargain_asking_price')} /></div>
                  <div><label className={labelCls}>Placeholder</label><input type="text" {...tf('bargain_placeholder')} /></div>
                  <div><label className={labelCls}>Cancel</label><input type="text" {...tf('bargain_cancel')} /></div>
                  <div><label className={labelCls}>Make Offer</label><input type="text" {...tf('bargain_make_offer')} /></div>
                  <div><label className={labelCls}>Try Again</label><input type="text" {...tf('bargain_try_again')} /></div>
                  <div><label className={labelCls}>Full Price</label><input type="text" {...tf('bargain_full_price')} /></div>
                  <div className="col-span-2"><label className={labelCls}>Tip Text</label><input type="text" {...tf('bargain_tip')} /></div>
                </div>
              </div>

              {/* Checkout */}
              <div className={sectionCls}>
                <h5 className={headingCls}>🧾 Checkout & Basket</h5>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className={labelCls}>Basket Title</label><input type="text" {...tf('checkout_title')} /></div>
                  <div><label className={labelCls}>For Recipe ({'{recipe}'})</label><input type="text" {...tf('checkout_for_recipe')} /></div>
                  <div><label className={labelCls}>Extra Items Label</label><input type="text" {...tf('checkout_extra_items')} /></div>
                  <div><label className={labelCls}>Empty Basket</label><input type="text" {...tf('checkout_empty')} /></div>
                  <div><label className={labelCls}>Total Label</label><input type="text" {...tf('checkout_total')} /></div>
                  <div><label className={labelCls}>Analyze Button</label><input type="text" {...tf('checkout_analyze')} /></div>
                  <div><label className={labelCls}>Missing ({'{count}'})</label><input type="text" {...tf('checkout_missing')} /></div>
                  <div><label className={labelCls}>All Collected</label><input type="text" {...tf('checkout_all_collected')} /></div>
                  <div><label className={labelCls}>Start Cooking</label><input type="text" {...tf('checkout_start_cooking')} /></div>
                  <div><label className={labelCls}>Continue Shopping</label><input type="text" {...tf('checkout_continue')} /></div>
                  <div><label className={labelCls}>Remove Button</label><input type="text" {...tf('checkout_remove')} /></div>
                </div>
              </div>

              {/* Success */}
              <div className={sectionCls}>
                <h5 className={headingCls}>🎉 Success / Cooking Complete</h5>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className={labelCls}>Title</label><input type="text" {...tf('success_title')} /></div>
                  <div><label className={labelCls}>Total Spent</label><input type="text" {...tf('success_total_spent')} /></div>
                  <div><label className={labelCls}>Money Saved</label><input type="text" {...tf('success_money_saved')} /></div>
                  <div><label className={labelCls}>Items Bought</label><input type="text" {...tf('success_items_bought')} /></div>
                  <div><label className={labelCls}>XP Earned</label><input type="text" {...tf('success_xp_earned')} /></div>
                  <div><label className={labelCls}>Great Bargain Title</label><input type="text" {...tf('success_great_bargain')} /></div>
                  <div className="col-span-2"><label className={labelCls}>Great Bargain Description</label><input type="text" {...tf('success_great_bargain_desc')} /></div>
                  <div><label className={labelCls}>How to Cook Button</label><input type="text" {...tf('success_how_to_cook')} /></div>
                  <div><label className={labelCls}>Cook Another Button</label><input type="text" {...tf('success_cook_another')} /></div>
                </div>
              </div>

              {/* Cooking Instructions */}
              <div className={sectionCls}>
                <h5 className={headingCls}>👨‍🍳 Cooking Instructions</h5>
                <div className="grid grid-cols-3 gap-2">
                  <div><label className={labelCls}>Prep Time</label><input type="text" {...tf('cooking_prep_time')} /></div>
                  <div><label className={labelCls}>Cook Time</label><input type="text" {...tf('cooking_cook_time')} /></div>
                  <div><label className={labelCls}>Servings</label><input type="text" {...tf('cooking_servings')} /></div>
                  <div><label className={labelCls}>Instructions Title</label><input type="text" {...tf('cooking_instructions_title')} /></div>
                  <div><label className={labelCls}>Pro Tips</label><input type="text" {...tf('cooking_pro_tips')} /></div>
                </div>
              </div>

              {/* Progress */}
              <div className={sectionCls}>
                <h5 className={headingCls}>📊 Progress Modal</h5>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className={labelCls}>Title</label><input type="text" {...tf('progress_title')} /></div>
                  <div><label className={labelCls}>Recipes Cooked</label><input type="text" {...tf('progress_recipes_cooked')} /></div>
                  <div><label className={labelCls}>DH Saved</label><input type="text" {...tf('progress_dh_saved')} /></div>
                  <div><label className={labelCls}>Bargains Won</label><input type="text" {...tf('progress_bargains_won')} /></div>
                  <div><label className={labelCls}>DH Spent</label><input type="text" {...tf('progress_dh_spent')} /></div>
                  <div><label className={labelCls}>Recipes List Title</label><input type="text" {...tf('progress_recipes_list_title')} /></div>
                  <div><label className={labelCls}>No Recipes Text</label><input type="text" {...tf('progress_no_recipes')} /></div>
                  <div><label className={labelCls}>No Recipes Hint</label><input type="text" {...tf('progress_no_recipes_hint')} /></div>
                  <div><label className={labelCls}>Bargain Skill Title</label><input type="text" {...tf('progress_bargain_skill')} /></div>
                  <div><label className={labelCls}>Master Level</label><input type="text" {...tf('progress_skill_master')} /></div>
                  <div><label className={labelCls}>Skilled Level</label><input type="text" {...tf('progress_skill_skilled')} /></div>
                  <div><label className={labelCls}>Getting Better</label><input type="text" {...tf('progress_skill_getting_better')} /></div>
                  <div><label className={labelCls}>Beginner</label><input type="text" {...tf('progress_skill_beginner')} /></div>
                </div>
              </div>

              {/* Warnings */}
              <div className={sectionCls}>
                <h5 className={headingCls}>⚠️ Warning Messages</h5>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className={labelCls}>Duplicate Title</label><input type="text" {...tf('warning_duplicate_title')} /></div>
                  <div><label className={labelCls}>Wrong Item Title</label><input type="text" {...tf('warning_wrong_item_title')} /></div>
                  <div><label className={labelCls}>No Money Title</label><input type="text" {...tf('warning_no_money_title')} /></div>
                  <div><label className={labelCls}>No Money Tip Header</label><input type="text" {...tf('warning_no_money_tip')} /></div>
                  <div><label className={labelCls}>Tip: Bargain</label><input type="text" {...tf('warning_no_money_bargain')} /></div>
                  <div><label className={labelCls}>Tip: Remove Items</label><input type="text" {...tf('warning_no_money_remove')} /></div>
                  <div><label className={labelCls}>Tip: Cheaper</label><input type="text" {...tf('warning_no_money_cheaper')} /></div>
                </div>
              </div>

              {/* Price Comparison & Analysis */}
              <div className={sectionCls}>
                <h5 className={headingCls}>📊 Comparison & Analysis</h5>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className={labelCls}>Compare Title</label><input type="text" {...tf('compare_title')} /></div>
                  <div><label className={labelCls}>Best Deal</label><input type="text" {...tf('compare_best_deal')} /></div>
                  <div><label className={labelCls}>Most Expensive</label><input type="text" {...tf('compare_most_expensive')} /></div>
                  <div><label className={labelCls}>Analysis Title</label><input type="text" {...tf('analysis_title')} /></div>
                  <div><label className={labelCls}>Analysis Subtitle</label><input type="text" {...tf('analysis_subtitle')} /></div>
                  <div><label className={labelCls}>Total Spent</label><input type="text" {...tf('analysis_total_spent')} /></div>
                  <div><label className={labelCls}>Could Have Saved</label><input type="text" {...tf('analysis_could_save')} /></div>
                  <div><label className={labelCls}>Not Needed Warning</label><input type="text" {...tf('analysis_not_needed')} /></div>
                </div>
              </div>

              {/* Delete Confirmation */}
              <div className={sectionCls}>
                <h5 className={headingCls}>🗑️ Delete Confirmation</h5>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className={labelCls}>Title</label><input type="text" {...tf('delete_confirm_title')} /></div>
                  <div><label className={labelCls}>Confirm Text</label><input type="text" {...tf('delete_confirm_text')} /></div>
                  <div className="col-span-2"><label className={labelCls}>Note</label><input type="text" {...tf('delete_confirm_note')} /></div>
                </div>
              </div>

              {/* Help */}
              <div className={sectionCls}>
                <h5 className={headingCls}>❓ Help & Tutorial</h5>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className={labelCls}>Help Modal Title</label><input type="text" {...tf('help_title')} /></div>
                    <div><label className={labelCls}>Help Button Text</label><input type="text" {...tf('help_button')} /></div>
                  </div>
                  <div>
                    <label className={labelCls}>Help Content (Markdown supported)</label>
                    <textarea {...tf('help_text')} rows="8" />
                  </div>
                </div>
              </div>

              {/* Quiz & Merchant Messages */}
              <div className={sectionCls}>
                <h5 className={headingCls}>🧠 Quiz & Merchant Messages</h5>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className={labelCls}>Merchant Asks (use {'{merchant}'})</label><input type="text" {...tf('quiz_merchant_asks')} /></div>
                  <div><label className={labelCls}>How Much Question (use {'{recipe}'})</label><input type="text" {...tf('quiz_how_much')} /></div>
                  <div><label className={labelCls}>Correct Answer</label><input type="text" {...tf('quiz_correct')} /></div>
                  <div><label className={labelCls}>Correct Detail (use {'{quantity}'} {'{unit}'} {'{item}'})</label><input type="text" {...tf('quiz_correct_detail')} /></div>
                  <div><label className={labelCls}>Wrong Answer</label><input type="text" {...tf('quiz_wrong')} /></div>
                  <div><label className={labelCls}>You Selected (use {'{quantity}'} {'{unit}'})</label><input type="text" {...tf('quiz_you_selected')} /></div>
                  <div className="col-span-2"><label className={labelCls}>Hint (use {'{quantity}'} {'{unit}'} {'{item}'} {'{recipe}'})</label><input type="text" {...tf('quiz_hint')} /></div>
                  <div><label className={labelCls}>Remember Text</label><input type="text" {...tf('quiz_remember')} /></div>
                  <div><label className={labelCls}>Continue Button</label><input type="text" {...tf('quiz_continue')} /></div>
                  <div><label className={labelCls}>Try Again Button</label><input type="text" {...tf('quiz_try_again')} /></div>
                  <div className="col-span-2"><label className={labelCls}>Merchant No Items Message</label><input type="text" {...tf('merchant_no_items')} /></div>
                  <div className="col-span-2"><label className={labelCls}>Merchant Welcome ({'{qty}'}, {'{item}'}, {'{recipe}'})</label><input type="text" {...tf('merchant_welcome')} /></div>
                  <div className="col-span-2"><label className={labelCls}>Merchant Redirect ({'{item}'}, {'{item_darija}'}, {'{other_merchant}'})</label><input type="text" {...tf('merchant_redirect')} /></div>
                  <div className="col-span-2"><label className={labelCls}>Merchant All Collected ({'{recipe}'})</label><input type="text" {...tf('merchant_all_collected')} /></div>
                </div>
              </div>

              {/* Legacy / Ready to Shop */}
              <div className={sectionCls}>
                <h5 className={headingCls}>📦 Ready to Shop (Legacy)</h5>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className={labelCls}>Title</label><input type="text" {...tf('ready_to_shop_title')} /></div>
                  <div><label className={labelCls}>Description</label><input type="text" {...tf('ready_to_shop_desc')} /></div>
                </div>
              </div>
            </div>
          </div>
          );
        })()}


        {/* Quizzes Tab */}
        {activeSubTab === 'quizzes' && (
          <QuizzesTab
            quizzes={quizzes}
            onEdit={(quiz) => {
              setEditingItem(quiz);
              setEditType('quiz');
              setShowEditModal(true);
            }}
            onCreate={() => {
              setEditingItem({ difficulty: 'easy', category: 'darija', reward: 30, penalty: 10, options: ['', '', '', ''], correct_index: 0, is_active: true });
              setEditType('quiz');
              setIsCreating(true);
              setShowEditModal(true);
            }}
            onDelete={async (id) => {
              if (!window.confirm('Delete this quiz?')) return;
              try {
                await fetch(`${API}/admin/souk-clothes/quizzes/${id}`, {
                  method: 'DELETE',
                  headers: { 'Authorization': `Bearer ${adminToken}` }
                });
                showNotification?.('Quiz deleted', 'success');
                loadQuizzes();
              } catch (error) {
                showNotification?.('Failed to delete quiz', 'error');
              }
            }}
            isDarkMode={isDarkMode}
          />
        )}

        {/* Cultural Tips Tab */}
        {activeSubTab === 'tips' && (
          <CulturalTipsTab
            tips={culturalTips}
            onEdit={(tip) => {
              setEditingItem(tip);
              setEditType('tip');
              setShowEditModal(true);
            }}
            onCreate={() => {
              setEditingItem({ emoji: '💡', category: 'general', is_active: true });
              setEditType('tip');
              setIsCreating(true);
              setShowEditModal(true);
            }}
            onDelete={async (id) => {
              if (!window.confirm('Delete this tip?')) return;
              try {
                await fetch(`${API}/admin/souk-clothes/cultural-tips/${id}`, {
                  method: 'DELETE',
                  headers: { 'Authorization': `Bearer ${adminToken}` }
                });
                showNotification?.('Tip deleted', 'success');
                loadCulturalTips();
              } catch (error) {
                showNotification?.('Failed to delete tip', 'error');
              }
            }}
            isDarkMode={isDarkMode}
          />
        )}

        {/* Darija Phrases Tab */}
        {activeSubTab === 'phrases' && (
          <DarijaPhrasesTab
            phrases={darijaPhras}
            onEdit={(phrase) => {
              setEditingItem(phrase);
              setEditType('phrase');
              setShowEditModal(true);
            }}
            onCreate={() => {
              setEditingItem({ emoji: '💬', category: 'shopping', is_active: true });
              setEditType('phrase');
              setIsCreating(true);
              setShowEditModal(true);
            }}
            onDelete={async (id) => {
              if (!window.confirm('Delete this phrase?')) return;
              try {
                await fetch(`${API}/admin/souk-clothes/darija-phrases/${id}`, {
                  method: 'DELETE',
                  headers: { 'Authorization': `Bearer ${adminToken}` }
                });
                showNotification?.('Phrase deleted', 'success');
                loadDarijaPhrases();
              } catch (error) {
                showNotification?.('Failed to delete phrase', 'error');
              }
            }}
            isDarkMode={isDarkMode}
          />
        )}

        {/* Wheel Settings Tab */}
        {activeSubTab === 'wheel' && (
          <WheelSettingsTab
            settings={wheelSettings}
            onChange={setWheelSettings}
            onSave={saveWheelSettings}
            isDarkMode={isDarkMode}
          />
        )}

        {/* Analytics Tab */}
        {activeSubTab === 'analytics' && (
          <AnalyticsTab
            analytics={analytics}
            isDarkMode={isDarkMode}
          />
        )}

        {/* Statistics Tab */}
        {activeSubTab === 'stats' && (
          <div className="space-y-6">
            <h4 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Game Statistics</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                <div className="text-4xl mb-2">🍽️</div>
                <p className="text-3xl font-bold">{recipes.length}</p>
                <p className="opacity-90">Total Recipes</p>
              </div>
              <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
                <div className="text-4xl mb-2">🥕</div>
                <p className="text-3xl font-bold">{items.length}</p>
                <p className="opacity-90">Total Items</p>
              </div>
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
                <div className="text-4xl mb-2">👨‍💼</div>
                <p className="text-3xl font-bold">{merchants.length}</p>
                <p className="opacity-90">Total Merchants</p>
              </div>
            </div>

            <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'}`}>
              <h5 className={`font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Items by Category</h5>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {(gameSettings?.merchant_categories || ['Vegetable', 'Meat', 'Spice', 'Shop']).map(category => {
                  const count = items.filter(i => i.category === category).length;
                  return (
                    <div key={category} className={`rounded-lg p-4 text-center ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
                      <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{count}</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{category}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        </div>
        {/* End Right Content Area */}
      </div>
      {/* End Main Layout */}

      {/* Edit Modal */}
      {showEditModal && editingItem && (['quiz', 'tip', 'phrase'].includes(editType)) && (
        <GamificationEditModal
          item={editingItem}
          setItem={setEditingItem}
          onSave={handleGamificationSave}
          onClose={() => {
            setShowEditModal(false);
            setEditingItem(null);
            setEditType('');
          }}
          type={editType}
          isDarkMode={isDarkMode}
        />
      )}

      {showEditModal && editingItem && !(['quiz', 'tip', 'phrase'].includes(editType)) && (
        <EditModal
          item={editingItem}
          setItem={setEditingItem}
          onSave={handleSave}
          onClose={() => {
            setShowEditModal(false);
            setEditingItem(null);
          }}
          isCreating={isCreating}
          type={activeSubTab}
          isDarkMode={isDarkMode}
          items={items}
          gameSettings={gameSettings}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-md w-full p-6 rounded-xl shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-center mb-6">
              <div className="text-6xl mb-4 animate-bounce">🗑️</div>
              <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Delete Confirmation
              </h3>
              <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Are you sure you want to delete:
              </p>
              <p className="text-xl font-bold text-red-600 mt-2">
                "{showDeleteConfirm.name}"
              </p>
              <p className={`text-sm mt-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                This action cannot be undone!
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className={`flex-1 px-4 py-3 rounded-lg font-bold transition-all ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold transition-all"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Edit Modal Component
const EditModal = ({ item, setItem, onSave, onClose, isCreating, type, isDarkMode, items, gameSettings }) => {
  const [activeTab, setActiveTab] = React.useState('basic');

  // Add ingredient to recipe
  const addIngredient = () => {
    const currentIngredients = item.required_items || [];
    setItem({
      ...item,
      required_items: [
        ...currentIngredients,
        { item_id: '', quantity: 1, unit: 'kg' }
      ]
    });
  };

  const updateIngredient = (index, field, value) => {
    const updatedIngredients = [...(item.required_items || [])];
    updatedIngredients[index] = {
      ...updatedIngredients[index],
      [field]: value
    };
    setItem({ ...item, required_items: updatedIngredients });
  };

  const removeIngredient = (index) => {
    const updatedIngredients = (item.required_items || []).filter((_, i) => i !== index);
    setItem({ ...item, required_items: updatedIngredients });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className={`w-full max-w-4xl rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className={`sticky top-0 p-6 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} z-10`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {isCreating ? 'Create New' : 'Edit'} {type === 'recipes' ? 'Recipe' : type === 'items' ? 'Item' : 'Merchant'}
            </h3>
            <button
              onClick={onClose}
              className={`text-2xl ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}
            >
              ×
            </button>
          </div>

          {/* Tabs for Recipe */}
          {type === 'recipes' && (
            <div className="flex gap-2 overflow-x-auto">
              {[
                { id: 'basic', label: 'Basic Info', icon: '📝' },
                { id: 'ingredients', label: 'Ingredients', icon: '🥕' },
                { id: 'instructions', label: 'Instructions', icon: '👨‍🍳' },
                { id: 'details', label: 'Details', icon: '⚙️' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 space-y-4">
          {/* Recipe Fields - Tabbed */}
          {type === 'recipes' && (
            <>
              {/* Basic Info Tab */}
              {activeTab === 'basic' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name (English) *</label>
                      <input
                        type="text"
                        value={item.name_english || ''}
                        onChange={(e) => setItem({...item, name_english: e.target.value})}
                        placeholder="e.g., Chicken Tajine"
                        className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name (Darija) *</label>
                      <input
                        type="text"
                        value={item.name_darija || ''}
                        onChange={(e) => setItem({...item, name_darija: e.target.value})}
                        placeholder="e.g., Tajine Djej"
                        className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name (Arabic)</label>
                    <input
                      type="text"
                      value={item.name_arabic || ''}
                      onChange={(e) => setItem({...item, name_arabic: e.target.value})}
                      placeholder="طاجين الدجاج"
                      className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
                    <textarea
                      value={item.description || ''}
                      onChange={(e) => setItem({...item, description: e.target.value})}
                      rows="3"
                      placeholder="A brief description of the recipe..."
                      className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>

                  {/* Recipe Image URL with preview */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Image URL (Dropbox)</label>
                    <input
                      type="text"
                      value={item.image_url || ''}
                      onChange={(e) => setItem({...item, image_url: e.target.value})}
                      placeholder="https://dl.dropboxusercontent.com/...?raw=1"
                      className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    />
                    {item.image_url && (
                      <div className={`mt-2 p-2 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                        <img src={item.image_url} alt="Recipe preview" className="max-h-40 object-contain mx-auto rounded" onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category *</label>
                      <select
                        value={item.category || 'Main Dish'}
                        onChange={(e) => setItem({...item, category: e.target.value})}
                        className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      >
                        <option>Main Dish</option>
                        <option>Soup</option>
                        <option>Breakfast</option>
                        <option>Bread</option>
                        <option>Dessert</option>
                        <option>Beverage</option>
                      </select>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Difficulty *</label>
                      <select
                        value={item.difficulty || 'Medium'}
                        onChange={(e) => setItem({...item, difficulty: e.target.value})}
                        className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      >
                        <option>Easy</option>
                        <option>Medium</option>
                        <option>Hard</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Ingredients Tab */}
              {activeTab === 'ingredients' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      Required Ingredients ({(item.required_items || []).length})
                    </h4>
                    <button
                      onClick={addIngredient}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                    >
                      + Add Ingredient
                    </button>
                  </div>

                  {(item.required_items || []).length === 0 && (
                    <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <p>No ingredients added yet</p>
                      <p className="text-sm mt-2">Click "Add Ingredient" to start</p>
                    </div>
                  )}

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {(item.required_items || []).map((ingredient, index) => (
                      <div key={index} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="grid grid-cols-12 gap-3 items-start">
                          <div className="col-span-6">
                            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Item *</label>
                            <select
                              value={ingredient.item_id || ''}
                              onChange={(e) => updateIngredient(index, 'item_id', e.target.value)}
                              className={`w-full px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            >
                              <option value="">Select item...</option>
                              {items.map(item => (
                                <option key={item.id} value={item.id}>
                                  {item.name_english} ({item.category})
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-span-2">
                            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Qty *</label>
                            <input
                              type="number"
                              step="0.1"
                              value={ingredient.quantity || ''}
                              onChange={(e) => updateIngredient(index, 'quantity', parseFloat(e.target.value))}
                              className={`w-full px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            />
                          </div>
                          <div className="col-span-3">
                            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Unit *</label>
                            <select
                              value={ingredient.unit || 'kg'}
                              onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                              className={`w-full px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            >
                              <option>kg</option>
                              <option>g</option>
                              <option>piece</option>
                              <option>cup</option>
                              <option>tbsp</option>
                              <option>tsp</option>
                              <option>bunch</option>
                              <option>bottle</option>
                            </select>
                          </div>
                          <div className="col-span-1 flex items-end">
                            <button
                              onClick={() => removeIngredient(index)}
                              className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                              title="Remove"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Instructions Tab */}
              {activeTab === 'instructions' && (
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Cooking Instructions
                    </label>
                    <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Enter step-by-step instructions. Use numbered steps (1., 2., 3., etc.) for clarity.
                    </p>
                    <textarea
                      value={item.cooking_instructions || ''}
                      onChange={(e) => setItem({...item, cooking_instructions: e.target.value})}
                      rows="15"
                      placeholder="1. Heat oil in a large pot...&#10;2. Add onions and sauté...&#10;3. ..."
                      className={`w-full px-3 py-2 rounded border font-mono text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>
                </div>
              )}

              {/* Details Tab */}
              {activeTab === 'details' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Cooking Time (minutes) *</label>
                      <input
                        type="number"
                        value={item.cooking_time_minutes || 30}
                        onChange={(e) => setItem({...item, cooking_time_minutes: parseInt(e.target.value)})}
                        className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Serves (people) *</label>
                      <input
                        type="number"
                        value={item.serves || 4}
                        onChange={(e) => setItem({...item, serves: parseInt(e.target.value)})}
                        className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Estimated Cost (DH) *</label>
                      <input
                        type="number"
                        step="0.1"
                        value={item.estimated_cost_dh || 0}
                        onChange={(e) => setItem({...item, estimated_cost_dh: parseFloat(e.target.value)})}
                        className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      />
                    </div>
                  </div>

                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900' : 'bg-blue-50'}`}>
                    <h5 className={`font-bold mb-2 ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>💡 Tips for Setting Cost:</h5>
                    <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      <li>• Estimate the total cost of all ingredients</li>
                      <li>• Players get 20% extra budget automatically</li>
                      <li>• Merchant prices vary ±15% from your estimate</li>
                      <li>• Keep it challenging but achievable with bargaining</li>
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Item Fields */}
          {type === 'items' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name (English)</label>
                  <input
                    type="text"
                    value={item.name_english || ''}
                    onChange={(e) => setItem({...item, name_english: e.target.value})}
                    className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name (Darija)</label>
                  <input
                    type="text"
                    value={item.name_darija || ''}
                    onChange={(e) => setItem({...item, name_darija: e.target.value})}
                    className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name (Arabic)</label>
                <input
                  type="text"
                  value={item.name_arabic || ''}
                  onChange={(e) => setItem({...item, name_arabic: e.target.value})}
                  className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                <select
                  value={item.category || 'Vegetable'}
                  onChange={(e) => setItem({...item, category: e.target.value})}
                  className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  {(gameSettings?.merchant_categories || ['Vegetable', 'Meat', 'Spice', 'Shop']).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Price (DH)</label>
                  <input
                    type="number"
                    value={item.base_price_tokens || 0}
                    onChange={(e) => setItem({...item, base_price_tokens: parseFloat(e.target.value)})}
                    className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Unit</label>
                  <input
                    type="text"
                    value={item.unit || ''}
                    onChange={(e) => setItem({...item, unit: e.target.value})}
                    placeholder="kg, piece, bottle..."
                    className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
              </div>

              {/* Image URL with preview */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Image URL (Dropbox)</label>
                <input
                  type="text"
                  value={item.image_url || ''}
                  onChange={(e) => setItem({...item, image_url: e.target.value})}
                  placeholder="https://dl.dropboxusercontent.com/...?raw=1"
                  className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
                {item.image_url && (
                  <div className={`mt-2 p-2 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                    <img src={item.image_url} alt="Preview" className="max-h-32 object-contain mx-auto rounded" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                )}
              </div>
            </>
          )}

          {/* Merchant Fields */}
          {type === 'merchants' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name</label>
                  <input
                    type="text"
                    value={item.name || ''}
                    onChange={(e) => setItem({...item, name: e.target.value})}
                    className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                  <select
                    value={item.category || 'Vegetable'}
                    onChange={(e) => setItem({...item, category: e.target.value})}
                    className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  >
                    {(gameSettings?.merchant_categories || ['Vegetable', 'Meat', 'Spice', 'Shop']).map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Personality</label>
                  <select
                    value={item.personality || 'helpful'}
                    onChange={(e) => setItem({...item, personality: e.target.value})}
                    className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  >
                    <option>helpful</option>
                    <option>rude</option>
                    <option>generous</option>
                    <option>comedic</option>
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Location</label>
                  <input
                    type="text"
                    value={item.location || ''}
                    onChange={(e) => setItem({...item, location: e.target.value})}
                    className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
              </div>

              {/* Avatar URL with preview */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Avatar Image URL (Dropbox)</label>
                <input
                  type="text"
                  value={item.avatar_url || ''}
                  onChange={(e) => setItem({...item, avatar_url: e.target.value})}
                  placeholder="https://dl.dropboxusercontent.com/...?raw=1"
                  className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
                {item.avatar_url && (
                  <div className={`mt-2 p-2 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                    <img src={item.avatar_url} alt="Avatar preview" className="w-20 h-20 rounded-full object-cover mx-auto" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
                <textarea
                  value={item.description || ''}
                  onChange={(e) => setItem({...item, description: e.target.value})}
                  rows="2"
                  placeholder="A brief description of this merchant..."
                  className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Greeting (Darija)</label>
                <input
                  type="text"
                  value={item.greeting_darija || ''}
                  onChange={(e) => setItem({...item, greeting_darija: e.target.value})}
                  className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Greeting (English)</label>
                <input
                  type="text"
                  value={item.greeting_english || ''}
                  onChange={(e) => setItem({...item, greeting_english: e.target.value})}
                  className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>

              {/* Bargaining config */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Min Discount %</label>
                  <input
                    type="number"
                    value={item.discount_range_min ?? 5}
                    onChange={(e) => setItem({...item, discount_range_min: parseInt(e.target.value)})}
                    className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Max Discount %</label>
                  <input
                    type="number"
                    value={item.discount_range_max ?? 25}
                    onChange={(e) => setItem({...item, discount_range_max: parseInt(e.target.value)})}
                    className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Price Multiplier</label>
                  <input
                    type="number"
                    step="0.1"
                    value={item.price_multiplier ?? 1.0}
                    onChange={(e) => setItem({...item, price_multiplier: parseFloat(e.target.value)})}
                    className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div className={`sticky bottom-0 p-6 border-t flex gap-3 ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <button
            onClick={onClose}
            className={`flex-1 px-4 py-2 rounded-lg font-medium ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:shadow-lg font-medium"
          >
            {isCreating ? 'Create' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};



// Gamification Edit Modal
const GamificationEditModal = ({ item, setItem, onSave, onClose, type, isDarkMode }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto`}>
        <h3 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {item.id ? 'Edit' : 'Create'} {type === 'quiz' ? 'Quiz' : type === 'tip' ? 'Cultural Tip' : 'Darija Phrase'}
        </h3>

        <div className="space-y-4">
          {type === 'quiz' && (
            <>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Question</label>
                <input
                  type="text"
                  value={item.question || ''}
                  onChange={(e) => setItem({...item, question: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border"
                  placeholder="Enter quiz question"
                />
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Options (4 options)</label>
                {[0, 1, 2, 3].map(idx => (
                  <input
                    key={idx}
                    type="text"
                    value={item.options?.[idx] || ''}
                    onChange={(e) => {
                      const newOptions = [...(item.options || ['', '', '', ''])];
                      newOptions[idx] = e.target.value;
                      setItem({...item, options: newOptions});
                    }}
                    className="w-full px-4 py-2 rounded-lg border mb-2"
                    placeholder={`Option ${idx + 1}`}
                  />
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Correct Answer (0-3)</label>
                  <input
                    type="number"
                    min="0"
                    max="3"
                    value={item.correct_index || 0}
                    onChange={(e) => setItem({...item, correct_index: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 rounded-lg border"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Difficulty</label>
                  <select
                    value={item.difficulty || 'easy'}
                    onChange={(e) => setItem({...item, difficulty: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                  <select
                    value={item.category || 'darija'}
                    onChange={(e) => setItem({...item, category: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border"
                  >
                    <option value="darija">Darija</option>
                    <option value="math">Math</option>
                    <option value="culture">Culture</option>
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Reward (DH)</label>
                  <input
                    type="number"
                    value={item.reward || 30}
                    onChange={(e) => setItem({...item, reward: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 rounded-lg border"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Penalty (DH)</label>
                  <input
                    type="number"
                    value={item.penalty || 10}
                    onChange={(e) => setItem({...item, penalty: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 rounded-lg border"
                  />
                </div>
              </div>
            </>
          )}

          {type === 'tip' && (
            <>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Title</label>
                <input
                  type="text"
                  value={item.title || ''}
                  onChange={(e) => setItem({...item, title: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border"
                  placeholder="Tip title"
                />
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Tip Content</label>
                <textarea
                  value={item.tip || ''}
                  onChange={(e) => setItem({...item, tip: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border"
                  rows="3"
                  placeholder="Enter the cultural tip"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Emoji</label>
                  <input
                    type="text"
                    value={item.emoji || '💡'}
                    onChange={(e) => setItem({...item, emoji: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border"
                    placeholder="💡"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                  <select
                    value={item.category || 'general'}
                    onChange={(e) => setItem({...item, category: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border"
                  >
                    <option value="general">General</option>
                    <option value="bargaining">Bargaining</option>
                    <option value="etiquette">Etiquette</option>
                    <option value="shopping">Shopping</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {type === 'phrase' && (
            <>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Darija Text</label>
                <input
                  type="text"
                  value={item.darija || ''}
                  onChange={(e) => setItem({...item, darija: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border"
                  placeholder="Bsh7al hadi?"
                />
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>English Translation</label>
                <input
                  type="text"
                  value={item.english || ''}
                  onChange={(e) => setItem({...item, english: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border"
                  placeholder="How much is this?"
                />
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Pronunciation</label>
                <input
                  type="text"
                  value={item.pronunciation || ''}
                  onChange={(e) => setItem({...item, pronunciation: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border"
                  placeholder="besh-hal ha-dee"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Emoji</label>
                  <input
                    type="text"
                    value={item.emoji || '💬'}
                    onChange={(e) => setItem({...item, emoji: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border"
                    placeholder="💬"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                  <select
                    value={item.category || 'shopping'}
                    onChange={(e) => setItem({...item, category: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border"
                  >
                    <option value="shopping">Shopping</option>
                    <option value="greeting">Greeting</option>
                    <option value="bargaining">Bargaining</option>
                    <option value="general">General</option>
                  </select>
                </div>
              </div>
            </>
          )}

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={item.is_active !== false}
                onChange={(e) => setItem({...item, is_active: e.target.checked})}
                className="w-5 h-5"
              />
              <span className={`font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Active</span>
            </label>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="flex-1 bg-gradient-to-r from-purple-500 to-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default SoukGameAdmin;
